import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import Button from 'reactstrap/lib/Button';
import Input from 'reactstrap/lib/Input';
import Modal from 'reactstrap/lib/Modal';
import Form from 'reactstrap/lib/Form';
import { FocusScope } from '@react-aria/focus';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { PAGE_SURVEY_RECIPE, RandomModel, RandomPaths } from 'client/data/models/random';
import { getStaticImageUrl } from 'client/utils/image-helpers';
import { addQueryParam } from 'site-modules/shared/utils/url-utils';
import { Storage } from 'site-modules/shared/utils/storage';
import { Link } from 'site-modules/shared/components/link/link';
import { ClientSideRender } from 'site-modules/shared/components/client-side-render/client-side-render';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';

import './canvas-promo.scss';

const FAB_IMAGE = getStaticImageUrl('/img/chat-canvas/fab-full.svg');
const TOOLTIP_LABEL = 'Open Ai Assistant';
const CANVAS_PROMO_TOOLTIP_KEY = 'canvas-promo-tooltip';
const CANVAS_PROMO_MODAL_KEY = 'canvas-promo';
const CANVAS_EXCLUDE_PAGE_NAMES = [
  'chat_canvas_assistant',
  'new_model_highlights',
  'used_model_highlights',
  'new_model_car_inventory_vin_detail_shopping',
  'used_model_car_inventory_vin_detail_shopping',
  'used_cars_tmv_appraiser_promotions',
  'data_privacy_request',
  'dealercenter',
  'dealercenter_products',
  'personalized_search',
];
const VAPORWARE_MODAL_CREATIVE_ID = 'chat-canvas-fab-survey';

export function checkIsCanvasTest(state, { isIframe = false } = {}) {
  return (
    !state?.mobile &&
    state?.request?.dest === (isIframe ? 'iframe' : 'document') &&
    !CANVAS_EXCLUDE_PAGE_NAMES.includes(state?.pageContext?.legacy?.pageName || state?.pageContext?.page?.name) &&
    !state?.request?.isSearchBot &&
    !state?.request?.isAkamaiCachePrefresh
  );
}

function CanvasPromoButtonUI({ canv79Recipe, pageSurveyRecipe, location, hasAdhesionBanner, isCanvasTest }) {
  const [isVaporwareModelOpen, toggleVaporwareModelOpen] = useToggle(false);
  const [isVaporwareButtonDisabled, setVaporwareButtonDisabled] = useState(true);
  const [isPromoClosed, setPromoClosed] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const sessionStorage = useRef();
  const localStorage = useRef();
  const vaporwareFeedbackRef = useRef();
  const isPromoEnabled = isCanvasTest && pageSurveyRecipe !== PAGE_SURVEY_RECIPE.MARKETING;

  useEffect(() => {
    localStorage.current = new Storage('localStorage');
    setPromoClosed(!!localStorage.current.get(CANVAS_PROMO_MODAL_KEY));
  }, []);

  useEffect(() => {
    if (!isPromoClosed && isPromoEnabled && canv79Recipe.includes('chal')) {
      sessionStorage.current = new Storage('sessionStorage');

      setShowTooltip(!sessionStorage.current.get(CANVAS_PROMO_TOOLTIP_KEY));
      sessionStorage.current.set(CANVAS_PROMO_TOOLTIP_KEY, '1');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPromoClosed]);

  const onVaporwareTextChange = useCallback(event => {
    setVaporwareButtonDisabled(!event?.target?.value?.length);
  }, []);

  const closeTooltip = useCallback(() => {
    setShowTooltip(false);
  }, []);

  const closeVaporwareModal = useCallback(
    event => {
      event.preventDefault();
      localStorage.current.set(CANVAS_PROMO_MODAL_KEY, '1');
      setPromoClosed(true);

      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ENGAGE_SURVEY,
          subaction_name: 'chat_canvas_fab_survey',
          creative_id: VAPORWARE_MODAL_CREATIVE_ID,
          action_category: TrackingConstant.USER_ACTION_CATEGORY,
          action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
          value: vaporwareFeedbackRef?.current?.value,
        },
      });

      toggleVaporwareModelOpen();
    },
    [toggleVaporwareModelOpen]
  );

  if (!isPromoEnabled || isPromoClosed) {
    return null;
  }

  const canvasUrl = addQueryParam('/ai-assistant/', 'page', `${location.pathname}${location.search}`);

  const tooltipFragment = showTooltip && (
    <ClientSideRender showTimeout={2000} placeholder={<div className="promo-tooltip visually-hidden" />}>
      <div className="promo-tooltip fade-in pos-a top-0 left-0">
        <div className="promo-tooltip-content medium">
          <div className="heading-6 mb-0_25 d-flex align-items-center">
            Edmunds Ai Assistant <span className="beta-tag text-white size-10 px-0_25 ms-0_5">BETA</span>
          </div>
          Ask anything from reviews to prices to deals. Click here to start.
        </div>
        <button
          onClick={closeTooltip}
          className="close-btn text-cool-gray-10 bg-transparent border-0 rounded pos-a top-0 right-0 mt-0_25 me-0_25 py-0_5 px-0_75"
        >
          <span className="visually-hidden">Close</span>
          <i className="icon-cross2 ms-0_25" aria-hidden />
        </button>
      </div>
    </ClientSideRender>
  );

  return (
    <Experiment name="canv-79-fab-v3" showDefault>
      <Recipe name="ctrl" isDefault />
      <Recipe name="chal1">
        <div
          className={classnames('canvas-promo pos-f bottom-0 left-0 ms-2 mb-2', {
            'has-adhesion': hasAdhesionBanner,
          })}
          data-tracking-parent="chat-canvas-fab"
        >
          <button
            className="border-0 px-0 py-0 bg-transparent"
            onClick={toggleVaporwareModelOpen}
            data-tracking-id="open_chat_canvas"
            title={showTooltip ? undefined : TOOLTIP_LABEL}
          >
            <span className="visually-hidden">{TOOLTIP_LABEL}</span>
            <img src={FAB_IMAGE} width={48} alt="" loading="lazy" />
          </button>
          {tooltipFragment}
        </div>
        <Modal
          className="canvas-vaporware-modal"
          contentClassName="px-1 py-1_5 rounded-8"
          isOpen={isVaporwareModelOpen}
          toggle={closeVaporwareModal}
          data-tracking-parent={VAPORWARE_MODAL_CREATIVE_ID}
          centered
        >
          <FocusScope contain>
            <Form onSubmit={closeVaporwareModal}>
              <div className="heading-4 mb-2 pe-2">
                Thanks for your interest. This feature is temporarily unavailable.
              </div>
              <div className="mb-1">
                We’d love your input. What would make an AI Assistant most helpful for you when shopping for a car?
              </div>
              <Input
                innerRef={vaporwareFeedbackRef}
                className="text-area-input rounded size-16 py-0_5 px-1 mb-1_5"
                name="vaporware-text"
                type="textarea"
                placeholder="Please explain..."
                onChange={onVaporwareTextChange}
                maxLength={200}
                rows={3}
              />
              <div className="d-flex justify-content-center">
                <Button
                  color="blue-50"
                  className="size-16 text-transform-none px-1_5 py-0_5"
                  disabled={isVaporwareButtonDisabled}
                >
                  Submit
                </Button>
              </div>
            </Form>
            <button
              className="size-20 text-cool-gray-50 pos-a top-0 right-0 mt-0_25 me-0_25 p-0_5 rounded border-0 bg-transparent"
              onClick={closeVaporwareModal}
              aria-hidden
            >
              <span className="visually-hidden">Close</span>
              <i className="icon-cross2" aria-hidden />
            </button>
          </FocusScope>
        </Modal>
      </Recipe>
      <Recipe name="chal2">
        <div
          className={classnames('canvas-promo pos-f bottom-0 left-0 ms-2 mb-2', {
            'has-adhesion': hasAdhesionBanner,
          })}
          data-tracking-parent="chat-canvas-fab"
        >
          <Link
            to={canvasUrl}
            rel="nofollow"
            data-tracking-id="open_chat_canvas"
            title={showTooltip ? undefined : TOOLTIP_LABEL}
          >
            <span className="visually-hidden">{TOOLTIP_LABEL}</span>
            <img src={FAB_IMAGE} width={48} alt="" loading="lazy" />
          </Link>
          {tooltipFragment}
        </div>
      </Recipe>
    </Experiment>
  );
}

CanvasPromoButtonUI.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  canv79Recipe: PropTypes.string,
  pageSurveyRecipe: PropTypes.string,
  hasAdhesionBanner: PropTypes.bool,
  isCanvasTest: PropTypes.bool,
};

CanvasPromoButtonUI.defaultProps = {
  canv79Recipe: null,
  pageSurveyRecipe: null,
  hasAdhesionBanner: false,
  isCanvasTest: false,
};

const mapStateToProps = state => ({
  isCanvasTest: checkIsCanvasTest(state),
  canv79Recipe: ExperimentUtil.getForcedOrAssignedRecipeName({
    state,
    campaignName: 'canv-79-fab-v3',
    defaultVal: 'ctrl',
  }),
  location: state?.pageContext?.location,
  hasAdhesionBanner: !!state?.ads?.adhesionBanner,
});

export const CanvasPromo = connect(mapStateToProps)(
  connectToModel(CanvasPromoButtonUI, {
    pageSurveyRecipe: bindToPath(RandomPaths.getPageSurveyRecipePath, RandomModel),
  })
);
